import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { gql, useLazyQuery } from "@apollo/client";
import { Typography, Grid, Box, Button, CircularProgress } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

import { Container } from "../../../commonStyles";
import { selectUser } from "../../../features/auth/authSlice";
import { IProject } from "../../../types/project";
import { useAppDispatch } from "../../../app/hooks";
import { showErrorSnackbar, showSuccessSnackbar } from "../../../features/snackbar/snackbarSlice";
import { IUser } from "../../../types/user";
import ReportCallTable from "./ReportCallTable";
import ReportUserTable from "./ReportUserTable";
import { IProjectTotal } from "../../userProfile/reporting/Reporting";

const ProjectReport = (props: ChildProps) => {
  const storeDispatch = useAppDispatch();
  const { project } = props;
  const user = useSelector(selectUser);

  const [dateStart, setDateStart] = useState<any>(new Date(new Date().setHours(0, 0, 0, 0)));
  const [dateEnd, setDateEnd] = useState<any>(new Date(new Date().setHours(23, 59, 59, 999)));
  const [show, setShow] = useState<boolean>(false);
  const [calls, setCalls] = useState<IPhoneTotal[]>([]);
  const [users, setUsers] = useState<IProjectTotal[][]>([]);

  const [downloadRdsReport, { loading: reportLoading }] = useLazyQuery(RDSDEBRIEF, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar("Email has been sent!"));
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar("Error getting stats"));
    },
  });

  const [getUserStats, { loading }] = useLazyQuery(GETUSERSTATS, {
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      setUsers(data.userStats.projects);
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar("Error getting stats"));
    },
  });

  const [getCallStats, { loading: callLoading }] = useLazyQuery(GETCALLSTATS, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      setCalls(data.callStats);
    },
    onError: (err) => {
      setCalls([]);
      storeDispatch(showErrorSnackbar(err.message));
    },
  });

  useEffect(() => {
    getCallStats({
      variables: {
        user: user?._id,
        project: project ? project._id : null,
        dateStart,
        dateEnd,
        userFilter: "All",
      },
    });
  }, [dateStart, dateEnd, project]);

  return (
    <Container>
      <Typography sx={{ mb: 0, alignSelf: "center" }} variant="h2" component="div" gutterBottom>
        <strong>Daily Report</strong>
      </Typography>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item sm={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label={"Start Date (MM/DD/YYYY)"}
              value={dateStart}
              onChange={(newValue) => {
                if (new Date(newValue).valueOf() > new Date(dateEnd).valueOf()) {
                  return storeDispatch(showErrorSnackbar("Start Date cannot be greater than End Date"));
                }
                setShow(false);
                setDateStart(newValue);
              }}
              sx={{ width: "100%" }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item sm={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label={"End Date (YYYY/MM/DD)"}
              value={dateEnd}
              onChange={(newValue) => {
                if (new Date(dateStart).valueOf() > new Date(newValue).valueOf()) {
                  return storeDispatch(showErrorSnackbar("Start Date cannot be greater than End Date"));
                }
                setShow(false);
                setDateEnd(newValue);
              }}
              sx={{ width: "100%" }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item sm={3} sx={{ display: "dlex" }}>
          <Box sx={{ mr: 2 }}>
            <Button onClick={() => setDateStart(new Date("January 1, 2023 00:00:00"))} variant="contained">
              Overall
            </Button>
          </Box>
        </Grid>
      </Grid>
      <ReportCallTable loading={callLoading} user={user!} phoneTotals={calls} dateStart={dateStart} dateEnd={dateEnd} />
      {users.length ? (
        <ReportUserTable
          loading={loading}
          user={user!}
          title={project?.name!}
          projectTotals={users[0]}
          dateStart={dateStart}
          dateEnd={dateEnd}
          project={project}
          users={[]}
        />
      ) : (
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => {
            getUserStats({
              variables: {
                user: [],
                project: project?._id,
                dateStart,
                dateEnd,
                userFilter: "All",
                debrief: false,
              },
            });
          }}
        >
          Show User Data
        </Button>
      )}
    </Container>
  );
};

interface ChildProps {
  project: IProject | null;
}

export interface IPhoneTotal {
  user: IUser | null;
  project: IProject | null;
  name: string;
  outboundCalls: number;
  inboundCalls: number;
  missedCalls: number;
  averageDurationOutbound: string;
  averageDurationInbound: string;
  callsReturned: number | null;
}

const RDSDEBRIEF = gql`
  query rdsDebriefReport($dateStart: Date!, $dateEnd: Date!, $email: Boolean!) {
    rdsDebriefReport(dateStart: $dateStart, dateEnd: $dateEnd, email: $email) {
      project
    }
  }
`;

const GETCALLSTATS = gql`
  query callStats($user: MongoID, $project: MongoID, $dateStart: Date!, $dateEnd: Date!, $userFilter: String) {
    callStats(user: $user, project: $project, dateStart: $dateStart, dateEnd: $dateEnd, userFilter: $userFilter) {
      user {
        _id
        fullName
      }
      name
      outboundCalls
      inboundCalls
      missedCalls
      averageDurationOutbound
      averageDurationInbound
      callsReturned
    }
  }
`;

const GETUSERSTATS = gql`
  query userStats($user: [MongoID], $project: MongoID, $dateStart: Date!, $dateEnd: Date!, $userFilter: String, $debrief: Boolean!) {
    userStats(user: $user, project: $project, dateStart: $dateStart, dateEnd: $dateEnd, userFilter: $userFilter, debrief: $debrief) {
      projects {
        id
        name
        user
        processesAssigned
        stepsCompleted
        stepsOverDue
        appointmentsCreated
        appointmentsBooked
        appointmentsSales
        cesInteractions
        handoverConnections
        appointmentsShowedUp
        emailsSent
        notes
      }
    }
  }
`;

export default ProjectReport;
